import React, { useState } from "react";
import { Box, Container, Typography, Button, Stack, Chip } from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useBooking } from "../context/BookingContext";

export default function CTASection() {
  const { openBookingModal } = useBooking();
  const [qrHovered, setQrHovered] = useState(false);

  const steps = [
    "Scan the QR code with your phone camera",
    "Download & register in 60 seconds",
    "Unlock any nearby EV instantly",
  ];

  return (
    <Box
      id="download"
      component="section"
      sx={{
        py: { xs: 10, md: 14 },
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(135deg, #0f172a 0%, #1B4ED8 50%, #0f172a 100%)",
        color: "#FFFFFF",
      }}
    >
      {/* Animated background blobs */}
      <Box
        sx={{
          position: "absolute",
          top: "-80px",
          right: "-80px",
          width: 400,
          height: 400,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(114,191,32,0.18) 0%, transparent 70%)",
          animation: "pulseBlob 6s ease-in-out infinite",
          "@keyframes pulseBlob": {
            "0%, 100%": { transform: "scale(1)", opacity: 0.7 },
            "50%": { transform: "scale(1.15)", opacity: 1 },
          },
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-100px",
          left: "-60px",
          width: 350,
          height: 350,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(27,78,216,0.25) 0%, transparent 70%)",
          animation: "pulseBlob 8s ease-in-out infinite reverse",
        }}
      />

      {/* Subtle grid overlay */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={{ xs: 6, md: 8 }}
        >
          {/* ── LEFT: Text Content ── */}
          <Box sx={{ maxWidth: 560, flex: 1 }}>
            <Chip
              icon={<SmartphoneIcon sx={{ fontSize: "16px !important", color: "#FFFFFF !important" }} />}
              label="DOWNLOAD APP"
              sx={{
                mb: 3,
                bgcolor: "secondary.main",
                color: "#FFFFFF",
                fontWeight: 800,
                fontSize: 12,
                letterSpacing: "0.08em",
                px: 1,
                height: 32,
                boxShadow: "0 4px 16px rgba(114,191,32,0.4)",
                "& .MuiChip-label": { px: 1 },
              }}
            />

            <Typography
              variant="h2"
              sx={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: { xs: 32, md: 44 },
                fontWeight: 800,
                color: "#FFFFFF",
                mb: 2,
                letterSpacing: "-0.03em",
                lineHeight: 1.15,
              }}
            >
              Your next EV ride is
              <Box
                component="span"
                sx={{
                  display: "block",
                  background:
                    "linear-gradient(90deg, #72BF20 0%, #a8e063 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                one scan away.
              </Box>
            </Typography>

            <Typography
              sx={{
                color: "rgba(255,255,255,0.75)",
                fontSize: 17,
                lineHeight: 1.75,
                mb: 4,
              }}
            >
              Point your phone camera at the QR code, download the RideX City
              app, and start riding any EV near you — no credit card required to
              try.
            </Typography>

            {/* Steps */}
            <Stack spacing={2} sx={{ mb: 5 }}>
              {steps.map((step, i) => (
                <Stack
                  key={i}
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                >
                  <CheckCircleIcon
                    sx={{ color: "secondary.main", fontSize: 20, flexShrink: 0 }}
                  />
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.85)",
                      fontSize: 15,
                      fontFamily: "'Space Grotesk', sans-serif",
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
                      {`Step ${i + 1}:`}
                    </Box>
                    {step}
                  </Typography>
                </Stack>
              ))}
            </Stack>

            {/* CTA Button */}
            <Button
              variant="contained"
              startIcon={<BoltIcon />}
              onClick={() => openBookingModal("Blaze")}
              sx={{
                bgcolor: "secondary.main",
                color: "#FFFFFF",
                px: 4,
                py: 1.6,
                borderRadius: "12px",
                fontSize: 16,
                fontWeight: 700,
                fontFamily: "'Space Grotesk', sans-serif",
                letterSpacing: "0.02em",
                boxShadow: "0 8px 24px rgba(114,191,32,0.40)",
                transition: "all 0.3s ease",
                "&:hover": {
                  bgcolor: "#5ea316",
                  transform: "translateY(-2px)",
                  boxShadow: "0 12px 32px rgba(114,191,32,0.55)",
                },
              }}
            >
              Book Your Ride Now
            </Button>
          </Box>

          {/* ── RIGHT: QR Code Card ── */}
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* Outer glow ring */}
            <Box
              onMouseEnter={() => setQrHovered(true)}
              onMouseLeave={() => setQrHovered(false)}
              sx={{
                position: "relative",
                p: { xs: "3px", md: "4px" },
                borderRadius: "24px",
                background: qrHovered
                  ? "linear-gradient(135deg, #72BF20, #1B4ED8, #72BF20)"
                  : "linear-gradient(135deg, rgba(114,191,32,0.6), rgba(27,78,216,0.6))",
                transition: "background 0.4s ease, box-shadow 0.4s ease",
                boxShadow: qrHovered
                  ? "0 0 40px rgba(114,191,32,0.6), 0 0 80px rgba(27,78,216,0.3)"
                  : "0 0 20px rgba(114,191,32,0.25)",
                animation: "rotateBorder 4s linear infinite",
                "@keyframes rotateBorder": {
                  "0%": { filter: "hue-rotate(0deg)" },
                  "100%": { filter: "hue-rotate(360deg)" },
                },
              }}
            >
              <Box
                sx={{
                  bgcolor: "#FFFFFF",
                  borderRadius: "22px",
                  p: 3,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  minWidth: { xs: 220, md: 260 },
                }}
              >
                {/* Scanner header */}
                <Stack direction="row" alignItems="center" spacing={1}>
                  <QrCodeScannerIcon sx={{ color: "#1B4ED8", fontSize: 22 }} />
                  <Typography
                    sx={{
                      color: "#1B4ED8",
                      fontWeight: 800,
                      fontSize: 13,
                      fontFamily: "'Space Grotesk', sans-serif",
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                    }}
                  >
                    Scan to Download
                  </Typography>
                </Stack>

                {/* QR Code image */}
                <Box
                  sx={{
                    position: "relative",
                    width: { xs: 180, md: 210 },
                    height: { xs: 180, md: 210 },
                    borderRadius: "12px",
                    overflow: "hidden",
                    border: "2px solid rgba(27,78,216,0.1)",
                  }}
                >
                  <Box
                    component="img"
                    src="/assets/images/app-qr-code.png"
                    alt="Scan to download RideX City app"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                      transition: "transform 0.4s ease",
                      transform: qrHovered ? "scale(1.05)" : "scale(1)",
                    }}
                  />

                  {/* Animated scan line */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      height: "2px",
                      background:
                        "linear-gradient(90deg, transparent, #72BF20, transparent)",
                      animation: "scanLine 2.5s ease-in-out infinite",
                      boxShadow: "0 0 8px rgba(114,191,32,0.8)",
                      "@keyframes scanLine": {
                        "0%": { top: "10%", opacity: 0 },
                        "20%": { opacity: 1 },
                        "80%": { opacity: 1 },
                        "100%": { top: "90%", opacity: 0 },
                      },
                    }}
                  />

                  {/* Corner brackets */}
                  {[
                    { top: 6, left: 6 },
                    { top: 6, right: 6 },
                    { bottom: 6, left: 6 },
                    { bottom: 6, right: 6 },
                  ].map((pos, i) => (
                    <Box
                      key={i}
                      sx={{
                        position: "absolute",
                        ...pos,
                        width: 18,
                        height: 18,
                        borderColor: "#72BF20",
                        borderStyle: "solid",
                        borderWidth: 0,
                        ...(pos.top !== undefined && pos.left !== undefined
                          ? { borderTopWidth: 3, borderLeftWidth: 3, borderRadius: "4px 0 0 0" }
                          : pos.top !== undefined
                            ? { borderTopWidth: 3, borderRightWidth: 3, borderRadius: "0 4px 0 0" }
                            : pos.left !== undefined
                              ? { borderBottomWidth: 3, borderLeftWidth: 3, borderRadius: "0 0 0 4px" }
                              : { borderBottomWidth: 3, borderRightWidth: 3, borderRadius: "0 0 4px 0" }),
                      }}
                    />
                  ))}
                </Box>

                {/* App name badge */}
                <Box
                  sx={{
                    bgcolor: "#f0f4ff",
                    borderRadius: "10px",
                    px: 2,
                    py: 1,
                    textAlign: "center",
                    width: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#1B4ED8",
                      fontWeight: 800,
                      fontSize: 15,
                      fontFamily: "'Space Grotesk', sans-serif",
                    }}
                  >
                    RideX City App
                  </Typography>
                  <Typography
                    sx={{
                      color: "#64748b",
                      fontSize: 12,
                      mt: 0.3,
                    }}
                  >
                    Free · Works on Android &amp; iOS
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Pulse indicator */}
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  bgcolor: "secondary.main",
                  animation: "pulseDot 1.5s ease-in-out infinite",
                  "@keyframes pulseDot": {
                    "0%, 100%": { opacity: 1, transform: "scale(1)" },
                    "50%": { opacity: 0.5, transform: "scale(1.5)" },
                  },
                }}
              />
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.7)",
                  fontSize: 13,
                  fontFamily: "'Space Grotesk', sans-serif",
                }}
              >
                Point camera · No app needed to scan
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}
